import React, { useState, useEffect } from 'react';
import './WorkspaceDocumentHistoryModal.css';

const WorkspaceDocumentHistoryModal = ({ history = [], documentName, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [expandedItems, setExpandedItems] = useState(new Set());
    const [copiedItem, setCopiedItem] = useState(null);
    const [processedHistoryData, setProcessedHistoryData] = useState([]);

    // Format date for display
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    // Copy text to clipboard
    const copyToClipboard = async (text, type) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedItem(type);
            setTimeout(() => setCopiedItem(null), 2000);
        } catch (error) {
            console.error('Failed to copy to clipboard:', error);
        }
    };

    // Toggle expanded state for history item
    const toggleExpanded = (version) => {
        const newExpandedItems = new Set(expandedItems);
        if (newExpandedItems.has(version)) {
            newExpandedItems.delete(version);
        } else {
            newExpandedItems.add(version);
        }
        setExpandedItems(newExpandedItems);
    };

    // Process history data when it changes (no decompression needed for workspace docs)
    useEffect(() => {
        if (history.length > 0) {
            setLoading(true);
            setError(null);
            
            try {
                // Process history items (no decompression needed)
                const processedHistory = history.map((item) => ({
                    ...item,
                    decompressedContent: item.content || '' // Content is already decompressed for workspace docs
                }));
                
                setProcessedHistoryData(processedHistory);
            } catch (error) {
                console.error('Error processing history data:', error);
                setError('Failed to process document history');
            } finally {
                setLoading(false);
            }
        } else {
            // No history data, not loading
            setLoading(false);
            setProcessedHistoryData([]);
        }
    }, [history]);

    // Reset state when modal closes
    useEffect(() => {
        return () => {
            setProcessedHistoryData([]);
            setError(null);
            setExpandedItems(new Set());
            setCopiedItem(null);
        };
    }, []);

    if (!onClose) return null;

    return (
        <div className="workspace-document-history-overlay" onClick={onClose}>
            <div className="workspace-document-history-modal" onClick={(e) => e.stopPropagation()}>
                <div className="WorkspaceDocumentHistoryModal__modal-header">
                    <h2><i className="fas fa-history"></i> Document History</h2>
                    <p>Version history for: <strong>{documentName}</strong></p>
                    <button className="modal-close-btn" onClick={onClose}>
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                <div className="WorkspaceDocumentHistoryModal__modal-content">
                    {loading && (
                        <div className="loading-section">
                            <div className="loading-spinner"></div>
                            <p>Loading document history...</p>
                        </div>
                    )}

                    {error && (
                        <div className="error-section">
                            <i className="fas fa-exclamation-triangle"></i>
                            <p>{error}</p>
                        </div>
                    )}

                    {!loading && !error && processedHistoryData.length === 0 && (
                        <div className="empty-section">
                            <i className="fas fa-file-alt"></i>
                            <p>No version history available for this document.</p>
                        </div>
                    )}

                    {!loading && !error && processedHistoryData.length > 0 && (
                        <div className="history-list">
                            {processedHistoryData.map((item) => {
                                const expanded = expandedItems.has(item.version);
                                return (
                                    <div key={item.version} className={`history-item${expanded ? ' expanded' : ''}`}>
                                        <div className="history-header" onClick={() => toggleExpanded(item.version)}>
                                            <div className="version-info">
                                                <span className="version-number">Version {item.version}</span>
                                                <span className="version-date">{formatDate(item.archived_at)}</span>
                                            </div>
                                            <div className="expand-icon">
                                                <i className={`fas fa-chevron-${expanded ? 'up' : 'down'}`}></i>
                                            </div>
                                        </div>
                                        <div className="history-details">
                                            <div className="detail-row">
                                                <label>Title:</label>
                                                <div className="detail-content">
                                                    <span>{item.document_title}</span>
                                                    <button
                                                        className={`copy-btn ${copiedItem === `title-${item.version}` ? 'copied' : ''}`}
                                                        onClick={() => copyToClipboard(item.document_title, `title-${item.version}`)}
                                                        title="Copy title"
                                                    >
                                                        <i className={`fas ${copiedItem === `title-${item.version}` ? 'fa-check' : 'fa-copy'}`}></i>
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="detail-row">
                                                <label>Description:</label>
                                                <div className="detail-content">
                                                    <span>{item.description || 'No description'}</span>
                                                    <button
                                                        className={`copy-btn ${copiedItem === `desc-${item.version}` ? 'copied' : ''}`}
                                                        onClick={() => copyToClipboard(item.description || '', `desc-${item.version}`)}
                                                        title="Copy description"
                                                    >
                                                        <i className={`fas ${copiedItem === `desc-${item.version}` ? 'fa-check' : 'fa-copy'}`}></i>
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="detail-row">
                                                <label>Document Type:</label>
                                                <div className="document-type-content">
                                                    <span className="document-type">{item.document_type}</span>
                                                </div>
                                            </div>

                                            <div className="detail-row">
                                                <label>Parent Document: &nbsp;</label>
                                                <div className="detail-content">
                                                    <span>{item.parent_document || 'None'}</span>
                                                </div>
                                            </div>

                                            <div className="detail-row">
                                                <label>Last Updated:</label>
                                                <div className="detail-content">
                                                    <span>{formatDate(item.last_updated_at)}</span>
                                                </div>
                                            </div>

                                            <div className="detail-row">
                                                <label>Last Updated By:</label>
                                                <div className="detail-content">
                                                    <span>{item.last_updated_by || 'Unknown'}</span>
                                                </div>
                                            </div>

                                            <div className="detail-row content-row">
                                                <label>Content:</label>
                                                <div className="content-preview">
                                                    <div 
                                                        className="content-html"
                                                        dangerouslySetInnerHTML={{ 
                                                            __html: item.decompressedContent || 'No content available' 
                                                        }}
                                                    />
                                                    <button
                                                        className={`copy-content-btn ${copiedItem === `content-${item.version}` ? 'copied' : ''}`}
                                                        onClick={() => copyToClipboard(item.decompressedContent || '', `content-${item.version}`)}
                                                        title="Copy HTML content"
                                                    >
                                                        <i className={`fas ${copiedItem === `content-${item.version}` ? 'fa-check' : 'fa-copy'}`}></i>
                                                        {copiedItem === `content-${item.version}` ? ' Copied!' : ' Copy HTML'}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="modal-footer">
                    <p><i className="fas fa-info-circle"></i> Click on any field's copy button to copy it to your clipboard</p>
                </div>
            </div>
        </div>
    );
};

export default WorkspaceDocumentHistoryModal;
